// Presenter helpers shared between the orders list table and the grid card.
// Both views render the same Order objects but in different layouts — these functions produce the
// formatted strings/values, so the two views stay in sync without duplicating logic.
import type { Order } from '@/types/types';
import { getAcronym } from '@/func/string/acronym';
import { getCurrencySign } from '@/locale/format';
import translator, { currentLanguage } from '@/locale/translator';

/* -------------------------------- COMPANY --------------------------------- */

export function companyLogoUrl(order: Order): string {
  return order?.company?.data?.logo_image?.data?.proxy?.url_thumbnail ?? '';
}

export function companyInitials(order: Order): string {
  const name = order?.company_name || order?.company?.data?.name || '';
  return name ? getAcronym(name).toUpperCase() : '';
}

/* -------------------------------- RUNTIME --------------------------------- */

export function runtimeProgress(order: Order): number {
  return order?.duration?.progress ?? 0;
}

export function runtimeText(order: Order): string {
  const days = order?.duration?.days ?? 0;
  const daysLeft = order?.duration?.days_left ?? 0;
  const elapsed = days - daysLeft;
  const total = days || '∞';
  return `${elapsed}/${total} ${translator.get('orders.list.days')}`;
}

/* ------------------------------- FORMATTING ------------------------------- */

// Thousands-separator formatter that follows the app locale (de-CH → 1'240, en-US → 1,240).
const countFormatter = new Intl.NumberFormat(currentLanguage.replace('_', '-'));

export function formatCount(n: number): string {
  return countFormatter.format(n);
}

/** Renders `1,240` / `—` for a nullable click/impression total. */
export function formatCountOrDash(n: number | null | undefined): string {
  return n ? formatCount(n) : '—';
}

/* --------------------------------- BUDGET --------------------------------- */

export function budgetText(order: Order): string {
  // budget_gross_total is stored in cents (same convention as OrderPills / OrderInfoTab).
  const cents = order?.budget_gross_total;
  if (cents == null) return '—';
  const sign = getCurrencySign(order?.budget_currency || '');
  return `${sign} ${(cents / 100).toFixed(2)}`;
}

/* ---------------------------------- CTR ----------------------------------- */

/** Numeric CTR as a percentage (0–100). Null when impressions is 0/missing. */
export function ctrValue(order: Order): number | null {
  const clicks = order?.stats_clicks_total ?? 0;
  const impressions = order?.stats_impressions_total ?? 0;
  if (!impressions) return null;
  return (clicks / impressions) * 100;
}

export function ctrText(order: Order): string {
  const v = ctrValue(order);
  return v == null ? '—' : `${v.toFixed(2)}%`;
}
